import React, { useState, useEffect } from "react";
import MaterialTable from "material-table";
import { Box } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import Popup from "../../../Base/views/Popup";
import { GetAllLeaveRequestsHelper } from "../../../Leave/helper/LeaveRequestHelper";
import { GetEmployeesHelper } from "../../../Employee/helper/EmployeeHelper";
import { GetShiftTimeConfigureHelper } from "../../../Leave/helper/ShiftTimeConfigureHelper";
import { DateConvertInDbFormat, ConvertDate, getUserId, getUserData, getDomain, getEmployeeId } from "../../../Base/helper/baseFunctions";

const OnLeavePopup = ({
  openPopup,
  setOpenPopup,
  data = null,
  fromDate = null,
  toDate = null,
  isKpi = false,
  dashboardFilters = null,
  selectedDate = null,
  domain_name = null,
  emp_user_id = null,
  reporting_manager_emp_id = null,
}) => {
  const [leaveData, setLeaveData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const effectiveFromDate = isKpi
    ? DateConvertInDbFormat()
    : fromDate
    ? typeof fromDate === "string"
      ? fromDate.slice(0, 10)
      : DateConvertInDbFormat(fromDate)
    : selectedDate
    ? typeof selectedDate === "string"
      ? selectedDate.slice(0, 10)
      : DateConvertInDbFormat(selectedDate)
    : DateConvertInDbFormat();

  const effectiveToDate = isKpi
    ? DateConvertInDbFormat()
    : toDate
    ? typeof toDate === "string"
      ? toDate.slice(0, 10)
      : DateConvertInDbFormat(toDate)
    : effectiveFromDate;

  useEffect(() => {
    if (openPopup) {
      if (Array.isArray(data) && data.length > 0) {
        setLeaveData(data);
      } else {
        setIsLoading(true);
        const effectiveReportingManager = reporting_manager_emp_id;

        let locJson = null;
        let sIds = null;
        let empTypeIds = null;

        if (isKpi && dashboardFilters) {
          if (Array.isArray(dashboardFilters.location) && dashboardFilters.location.length > 0) {
            const locList = dashboardFilters.location
              .map((item) => {
                const locId = typeof item === "object" && item !== null ? item.work_location_id : item;
                return locId ? { ats_work_location_id: String(locId) } : null;
              })
              .filter(Boolean);
            if (locList.length > 0) {
              locJson = JSON.stringify(locList);
            }
          }

          if (Array.isArray(dashboardFilters.shift) && dashboardFilters.shift.length > 0) {
            const ids = dashboardFilters.shift
              .map((s) => (typeof s === "object" && s !== null ? s.shift_roster_configuration_id : s))
              .filter(Boolean);
            if (ids.length > 0) {
              sIds = `{${ids.join(",")}}`;
            }
          }

          if (Array.isArray(dashboardFilters.employmentType) && dashboardFilters.employmentType.length > 0) {
            const ids = dashboardFilters.employmentType
              .map((e) => (typeof e === "object" && e !== null ? e.employmenttype_id : e))
              .filter(Boolean);
            if (ids.length > 0) {
              empTypeIds = `{${ids.join(",")}}`;
            }
          }
        }

        Promise.all([
          GetAllLeaveRequestsHelper({
            domain_name: domain_name || getDomain(),
            emp_user_id: emp_user_id || getUserId(),
            from_date: effectiveFromDate,
            to_date: effectiveToDate,
            reporting_manager_emp_id: effectiveReportingManager,
            ats_work_location_json: locJson,
            shift_ids: sIds,
            employment_type_ids: empTypeIds,
          }),
          GetEmployeesHelper({ is_active: true }),
          GetShiftTimeConfigureHelper({ domain_name: getDomain() }),
        ])
          .then(([leaveRes, empRes, shiftRes]) => {
            const rawList = (!leaveRes?.hasError && Array.isArray(leaveRes?.data?.data))
              ? leaveRes.data.data
              : Array.isArray(leaveRes?.data)
              ? leaveRes.data
              : [];
            const empList = empRes?.data?.data || [];
            const shiftList = shiftRes?.data?.data || [];
            const defaultShift = shiftList.find((s) => s.is_default === true) || shiftList[0];

            const formatted = rawList.map((item) => {
              const matchedEmp = empList.find(
                (e) => String(e?.employee_id || e?.employee_pk || e?.id) === String(item?.employee_id || item?.employee_fk)
              );

              let matchedShiftName = "N/A";
              if (matchedEmp?.work_hour_configuration_id) {
                const s = shiftList.find(
                  (shift) =>
                    String(shift.shift_roster_configuration_id) === String(matchedEmp.work_hour_configuration_id)
                );
                if (s) {
                  matchedShiftName = s.shift_name || "N/A";
                }
              }

              if (matchedShiftName === "N/A" && defaultShift) {
                matchedShiftName = defaultShift.shift_name || "N/A";
              }

              return {
                employee_name: item?.employee || item?.employee_name || "",
                employee_no: item?.employee_no && item?.employee_no !== "-" ? item.employee_no : (matchedEmp?.employee_no || "N/A"),
                business_unit: item?.business_unit && item?.business_unit !== "-" ? item.business_unit : (matchedEmp?.businessunit || "N/A"),
                shift_name: item?.shift_name && item?.shift_name !== "-" ? item.shift_name : (matchedShiftName || "N/A"),
                employment_type: item?.employment_type || item?.employmenttype_name || item?.employment_type_name || (matchedEmp?.employment_type || matchedEmp?.employmenttype_name || "N/A"),
                work_location_name: item?.work_location_name && item?.work_location_name !== "-" ? item.work_location_name : (matchedEmp?.work_location || "N/A"),
                leave_type: item?.leavetype || "",
                start_date: item?.from_date || "",
                end_date: item?.to_date || "",
                status: item?.status || "",
              };
            });

            setLeaveData(formatted);
          })
          .catch((err) => {
            console.error("Error loading approved team leave requests:", err);
            setLeaveData([]);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [
    openPopup,
    data,
    effectiveFromDate,
    effectiveToDate,
    isKpi,
    dashboardFilters,
    domain_name,
    emp_user_id,
    reporting_manager_emp_id,
  ]);

  const handleExport = () => {
    try {
      const headers = [
        { label: "Emp No", key: "employee_no" },
        { label: "Name", key: "employee_name" },
        { label: "Business Unit", key: "business_unit" },
        { label: "Shift Time", key: "shift_name" },
        { label: "Employment Type", key: "employment_type" },
        { label: "Work Location", key: "work_location_name" },
      ];

      const fromDateStr = isKpi
        ? ConvertDate(DateConvertInDbFormat())
        : effectiveFromDate
        ? ConvertDate(effectiveFromDate)
        : ConvertDate(DateConvertInDbFormat());

      const toDateStr = isKpi
        ? ConvertDate(DateConvertInDbFormat())
        : effectiveToDate
        ? ConvertDate(effectiveToDate)
        : fromDateStr;

      let reportPeriod = "On Leave Employees";
      if (fromDateStr && toDateStr) {
        reportPeriod =
          fromDateStr === toDateStr
            ? `On Leave Employees - Date: ${fromDateStr}`
            : `On Leave Employees - Period: ${fromDateStr} to ${toDateStr}`;
      } else if (fromDateStr) {
        reportPeriod = `On Leave Employees - Date: ${fromDateStr}`;
      }

      const csvData = [[`"${reportPeriod.replace(/"/g, '""')}"`], [], headers.map((h) => `"${h.label}"`)];

      (leaveData || []).forEach((row) => {
        const rowData = headers.map((h) => {
          const value = row[h.key];
          if (value === null || value === undefined) return '""';
          if (typeof value === "string") {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return `"${value}"`;
        });
        csvData.push(rowData);
      });

      const csv = csvData.map((row) => row.join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = `${reportPeriod.replace(/[/\\?%*:|"<>]/g, "-")}.csv`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
    }
  };

  const columns = [
    {
      title: "Emp No",
      field: "employee_no",
      editable: "never",
      width: "150px",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Name",
      field: "employee_name",
      editable: "never",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Business Unit",
      field: "business_unit",
      editable: "never",
      width: "150px",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Shift Time",
      field: "shift_name",
      editable: "never",
      width: "150px",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Employment Type",
      field: "employment_type",
      editable: "never",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
      render: (rowData) => rowData?.employment_type || "N/A",
    },
    {
      title: "Work Location",
      field: "work_location_name",
      editable: "never",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
  ];

  const fromDateFormatted = isKpi
    ? ConvertDate(DateConvertInDbFormat())
    : effectiveFromDate
    ? ConvertDate(effectiveFromDate)
    : ConvertDate(DateConvertInDbFormat());

  const toDateFormatted = isKpi
    ? ConvertDate(DateConvertInDbFormat())
    : effectiveToDate
    ? ConvertDate(effectiveToDate)
    : fromDateFormatted;

  const dateLabel =
    fromDateFormatted === toDateFormatted
      ? `Selected date : ${fromDateFormatted}`
      : `Selected date from ${fromDateFormatted} to ${toDateFormatted}`;

  return (
    <Popup
      fullWidth={true}
      maxWidth="md"
      title="On Leave"
      openPopup={openPopup}
      setOpenPopup={setOpenPopup}
      dialogContentSx={{ padding: "0px 10px 10px 10px" }}
    >
      <Box sx={{ width: "100%", pt: 1 }}>
        <MaterialTable
          title={<Box sx={{ fontSize: "13.5px", fontWeight: 600, color: "#555" }}>{dateLabel}</Box>}
          columns={columns}
          data={leaveData}
          isLoading={isLoading}
          actions={
            leaveData?.length > 0
              ? [
                  {
                    icon: () => <CloudDownloadIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />,
                    tooltip: "Export CSV",
                    isFreeAction: true,
                    onClick: handleExport,
                  },
                ]
              : []
          }
          options={{
            maxBodyHeight: "400px",
            minBodyHeight: "200px",
            rowStyle: { fontSize: "13px" },
            headerStyle: { padding: "10px 10px 10px 13px", fontWeight: "bold" },
            sorting: true,
            search: true,
            searchFieldAlignment: "right",
            searchAutoFocus: false,
            searchFieldVariant: "standard",
            paging: true,
            pageSizeOptions: [10, 25, 50],
            pageSize: 10,
            paginationType: "stepped",
            showFirstLastPageButtons: false,
            paginationPosition: "bottom",
            exportButton: false,
            columnsButton: false,
            showTitle: true,
          }}
          localization={{
            toolbar: {
              searchPlaceholder: "Search",
            },
          }}
        />
      </Box>
    </Popup>
  );
};

export default OnLeavePopup;
